/**
 * Display helpers (labels + badge variants) for issue-reporting enums. Keeps the Super-Admin UI
 * consistent and avoids scattering switch statements across components.
 */

import type { IssueCategory, IssueSeverity, IssueStatus } from '@/types/issue-reports';

type BadgeVariant = 'default' | 'secondary' | 'destructive' | 'outline';

export const SEVERITY_LABEL: Readonly<Record<IssueSeverity, string>> = {
  Low: 'Low',
  Medium: 'Medium',
  High: 'High',
  Critical: 'Critical',
};

export const CATEGORY_LABEL: Readonly<Record<IssueCategory, string>> = {
  Bug: 'Bug',
  UiVisual: 'UI / Visual',
  Performance: 'Performance',
  DataIncorrect: 'Incorrect data',
  FeatureRequest: 'Feature request',
  Other: 'Other',
};

export const STATUS_LABEL: Readonly<Record<IssueStatus, string>> = {
  New: 'New',
  Triaged: 'Triaged',
  InProgress: 'In progress',
  Resolved: 'Resolved',
  Closed: 'Closed',
  WontFix: "Won't fix",
};

export function severityBadgeVariant(s: IssueSeverity): BadgeVariant {
  switch (s) {
    case 'Critical':
    case 'High':
      return 'destructive';
    case 'Medium':
      return 'default';
    case 'Low':
      return 'secondary';
    default:
      return 'outline';
  }
}

export function statusBadgeVariant(s: IssueStatus): BadgeVariant {
  switch (s) {
    case 'New':
      return 'default';
    case 'Triaged':
    case 'InProgress':
      return 'secondary';
    case 'Resolved':
    case 'Closed':
      return 'outline';
    case 'WontFix':
      return 'destructive';
    default:
      return 'outline';
  }
}

/** Format an ISO timestamp as a compact local string; '—' when null/empty. */
export function formatDateTime(iso: string | null | undefined): string {
  if (iso === null || iso === undefined || iso === '') return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
}
